<template>
  <v-dialog v-model="dialog" max-width="500">
    <v-card>
      <v-card-title>
        <span class="headline">QR Code - {{ packageType?.name }}</span>
      </v-card-title>

      <v-card-text v-if="packageType">
        <v-container>
          <v-row>
            <!-- QR Code Display -->
            <v-col cols="12" class="text-center">
              <div v-if="loading" class="py-8">
                <v-progress-circular indeterminate color="primary" />
              </div>
              <div v-else-if="qrCodeImage" class="qr-code-container">
                <img 
                  :src="qrCodeImage" 
                  alt="QR Code"
                  class="qr-code-image"
                />
                <div class="mt-3">
                  <v-chip small outlined>
                    {{ packageType.name }}
                  </v-chip>
                </div>
              </div>
            </v-col>

            <!-- Package Details -->
            <v-col cols="12">
              <v-divider class="my-3" />
              <div class="package-details">
                <h4 class="subtitle-1 mb-2">Package Details</h4>
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="text-right font-weight-medium">Dimensions:</td>
                      <td>{{ packageType.length }}" × {{ packageType.width }}" × {{ packageType.height }}"</td>
                    </tr>
                    <tr v-if="packageType.weight">
                      <td class="text-right font-weight-medium">Weight:</td>
                      <td>{{ packageType.weight }} lbs</td>
                    </tr>
                    <tr>
                      <td class="text-right font-weight-medium">Volume:</td>
                      <td>{{ calculateVolume() }} cubic inches</td>
                    </tr>
                    <tr>
                      <td class="text-right font-weight-medium">Package ID:</td>
                      <td>{{ packageType.packageTypeId }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-col>

          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-btn
          text
          color="primary"
          @click="downloadQRCode"
          :disabled="!qrCodeImage"
        >
          <v-icon left>mdi-download</v-icon>
          Download
        </v-btn>
        <v-btn
          text
          color="primary"
          @click="printQRCode"
          :disabled="!qrCodeImage"
        >
          <v-icon left>mdi-printer</v-icon>
          Print
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'
import { PackageTypeService } from '@/services/PackageTypeService'

export default Vue.extend({
  name: 'QRCodePreviewDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    packageType: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      qrCodeImage: null,
      qrCodeData: null,
      packageService: null
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    packageType: {
      handler(newVal) {
        if (newVal && this.value) {
          this.loadQRCode(newVal)
        }
      },
      immediate: true
    },
    value(newVal) {
      if (newVal && this.packageType) {
        this.loadQRCode(this.packageType)
      }
    }
  },
  created() {
    this.packageService = new PackageTypeService()
  },
  methods: {
    async loadQRCode(packageType) {
      this.loading = true
      try {
        const response = await this.packageService.generateQRCode(packageType.packageTypeId)
        this.qrCodeImage = response.qrCode.data
        this.qrCodeData = response.qrCode.payload
      } catch (error) {
        console.error('Failed to load QR code:', error)
        this.$emit('show-snackbar', {
          text: 'Failed to load QR code',
          color: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    calculateVolume() {
      if (!this.packageType) return 0
      const { length, width, height } = this.packageType
      return Math.round(length * width * height * 100) / 100
    },
    downloadQRCode() {
      if (!this.qrCodeImage || !this.packageType) return

      // Create a temporary link element
      const link = document.createElement('a')
      link.href = this.qrCodeImage
      link.download = `package-qr-${this.packageType.name.replace(/\s+/g, '-').toLowerCase()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$emit('show-snackbar', {
        text: 'QR code downloaded',
        color: 'success'
      })
    },
    async printQRCode() {
      if (!this.qrCodeImage || !this.packageType) return

      try {
        // Create a print window with the QR code
        const printWindow = window.open('', '_blank', 'width=600,height=600')
        if (printWindow) {
          // Build the script separately to avoid template literal issues
          const printScript = '<script>' +
            'window.onload = function() {' +
            '  window.print();' +
            '  window.onafterprint = function() {' +
            '    window.close();' +
            '  }' +
            '}' +
            '<' + '/script>'
          
          const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <title>Print QR Code - ${this.packageType.name}</title>
              <style>
                body {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  font-family: Arial, sans-serif;
                  margin: 20px;
                }
                .qr-container {
                  text-align: center;
                  padding: 20px;
                  border: 2px solid #ddd;
                  border-radius: 8px;
                }
                .qr-code {
                  width: 300px;
                  height: 300px;
                  margin: 20px auto;
                }
                .package-name {
                  font-size: 18px;
                  font-weight: bold;
                  margin-top: 10px;
                }
                .package-dims {
                  font-size: 14px;
                  color: #666;
                  margin-top: 5px;
                }
                @media print {
                  body { margin: 0; }
                  .qr-container { border: none; }
                }
              </style>
            </head>
            <body>
              <div class="qr-container">
                <img src="${this.qrCodeImage}" class="qr-code" />
                <div class="package-name">${this.packageType.name}</div>
                <div class="package-dims">
                  ${this.packageType.length}" × ${this.packageType.width}" × ${this.packageType.height}"
                </div>
              </div>
              ${printScript}
            </body>
            </html>
          `
          printWindow.document.write(htmlContent)
          printWindow.document.close()
        }
      } catch (error) {
        console.error('Failed to print QR code:', error)
        this.$emit('show-snackbar', {
          text: 'Failed to print QR code',
          color: 'error'
        })
      }
    },
    close() {
      this.dialog = false
      this.qrCodeImage = null
      this.qrCodeData = null
    }
  }
})
</script>

<style scoped>
.qr-code-container {
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
}

.qr-code-image {
  max-width: 300px;
  height: auto;
  border: 2px solid #ddd;
  border-radius: 4px;
  background: white;
  padding: 10px;
}

.package-details {
  background: #fafafa;
  padding: 12px;
  border-radius: 4px;
}

/* Improve dialog readability */
>>> .v-card-title .headline {
  font-size: 20px !important;
  font-weight: 500 !important;
}

>>> .subtitle-1 {
  font-size: 16px !important;
  font-weight: 600 !important;
}

>>> .v-simple-table tbody td {
  font-size: 14px !important;
  padding: 8px 16px !important;
}

>>> .v-simple-table tbody td.font-weight-medium {
  font-weight: 600 !important;
}

>>> .v-chip {
  font-size: 13px !important;
  font-weight: 500 !important;
}

>>> .v-btn {
  font-size: 14px !important;
}

>>> .v-btn .v-icon {
  font-size: 18px !important;
}
</style>